import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../utils/api';
import {
  LoginFormInputs,
  AuthResponse,
  UserActivityDto,
  UserActivityList,
  UserSessionList,
} from '../dtos/auth';
import { ApiResponseError } from '../dtos/common';
import { accessToken, getAuthDetails } from '../utils/helper';
import Cookies from 'universal-cookie';

const cookies = new Cookies(null, { path: '/' });

interface AuthState {
  loginResponse: {
    data: AuthResponse | null;
    message: string;
    loginType: string;
  };
  userActivityList:
    | { data: UserActivityList[]; total: number; totalPages: number }
    | { data: []; total: 0; totalPages: 0 };
  userSessionList:
    | { data: UserSessionList[]; total: number; totalPages: number }
    | { data: []; total: 0; totalPages: 0 };
  forgotPasswordResponse: { message: string };
  resetPasswordResponse: { message: string };
  loading: boolean;
  error: string | null;
  csrfDetails: { data: string };
  tokenExpiry: { data: { verify: boolean; email: string } };
}

const initialState: AuthState = {
  userActivityList: { data: [], total: 0, totalPages: 0 },
  userSessionList: { data: [], total: 0, totalPages: 0 },
  loginResponse: { data: null, message: '', loginType: 'default' },
  forgotPasswordResponse: { message: '' },
  resetPasswordResponse: { message: '' },
  loading: false,
  error: null,
  csrfDetails: { data: '' },
  tokenExpiry: { data: { verify: false, email: '' } },
};

export const loginTrigger = createAsyncThunk('auth/login', async (data: LoginFormInputs) => {
  try {
    const response = await api.post<{
      data: AuthResponse | null;
      message: string;
      loginType: string;
    }>('/auth/login', data, { withCredentials: true });
    return response.data;
  } catch (error) {
    const apiError = error as ApiResponseError;
          return {
        data: null,
        message: apiError.response?.data?.message || 'Something Went Wrong!',
        loginType: 'default',
      };
  }
});

export const forgotPasswordTrigger = createAsyncThunk(
  '/auth/request-password',
  async (email: string) => {
    try {
      const response = await api.post<{ message: string }>(
        '/auth/request-password',
        { email: email },
        { withCredentials: true },
      );
      return response.data;
    } catch (error) {
      const apiError = error as ApiResponseError;
      return {
        message: apiError.response?.data?.message || 'Something Went Wrong!',
      };
    }
  },
);

export const resetPasswordTrigger = createAsyncThunk(
  '/auth/reset-password',
  async ({ resetToken, newPassword }: { resetToken: string; newPassword: string }) => {
    try {
      const response = await api.post<{ message: string }>(
        '/auth/reset-password',
        { resetToken, newPassword },
        { withCredentials: true },
      );
      return response.data;
    } catch (error) {
      const apiError = error as ApiResponseError;
      return {
        data: null,
        message: apiError.response?.data?.message || 'Something Went Wrong!',
      };
    }
  },
);

export const getCSRFToken = createAsyncThunk('auth/csrf-token', async () => {
  try {
    const response = await api.get(`/auth/csrf-token`, { withCredentials: true });
    return response.data;
  } catch (error) {
    console.log(error);
    return { data: '' };
  }
});

export const getTokenStatus = createAsyncThunk('auth/get-token/:id', async (token: string) => {
  try {
    const response = await api.get(`/auth/get-token/${token}`);
    return response.data;
  } catch (error) {
    console.log(error);
    return { data: { verify: false, email: '' } };
  }
});

export const otpVerification = createAsyncThunk(
  'auth/otp/verification',
  async ({ otp, token }: { otp: string; token: string }) => {
    try {
      const response = await api.post<{
        data: AuthResponse | null;
        message: string;
        loginType: string;
      }>(
        '/auth/otp/verification',
        {
          otp,
          token,
        },
        { withCredentials: true },
      );
      return response.data;
    } catch (error) {
      console.log(error);
      const apiError = error as ApiResponseError;
      return {
        data: null,
        message: apiError.response?.data?.message || 'Something Went Wrong!',
        loginType: 'default',
      };
    }
  },
);

export const addUserActivityTrigger = createAsyncThunk<
  { message: string },
  UserActivityDto
>('/auth/user-activity/add', async (request: UserActivityDto) => {
  try {
    const response = await api.post<{ message: string }>(
      `/auth/user-activity/add`,
      request,
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAuthDetails().accessToken}`,
          'x-csrf-token': cookies.get('XSRF-TOKEN'),
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error(error);
    return { message: 'Error adding user' };
  }
});

export const getAlluserActivityTrigger = createAsyncThunk(
  'user-activity',
  async ({
    page,
    limit,
    search,
    userId,
    sortOrder,
    sortField,
  }: {
    page?: number;
    limit?: number;
    search?: string;
    userId?: number;
    sortOrder?: string;
    sortField?: string;
  }) => {
    try {
      // Clean the sortField to remove any malformed characters
      const cleanSortField = sortField ? sortField.split(':')[0] : undefined;
      // Use snake_case to match iOS app standard

      const queryParams = new URLSearchParams();
      if (userId) queryParams.append('userId', userId.toString());
      if (page) queryParams.append('page', page.toString());
      if (limit) queryParams.append('limit', limit.toString());
      if (search) queryParams.append('search', search);
      if (sortOrder) queryParams.append('sortOrder', sortOrder);
      if (cleanSortField) queryParams.append('sortField', cleanSortField);

      const response = await api.get(`/user-activity?${queryParams}`, accessToken());
      return response.data;
    } catch (error) {
      console.log(error);
      return { data: [], totalPages: 0 };
    }
  },
);

export const addUserSessionTrigger = createAsyncThunk<{ message: string }>(
  '/auth/user-sessions/add',
  async () => {
    try {
      console.log(cookies.get('XSRF-TOKEN'), '---------------$$$$$--------------');
      const response = await api.post<{ message: string }>(
        `/auth/user-sessions/add`,
        { name: 'user-session' },
        {
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${getAuthDetails().accessToken}`,
            'x-csrf-token': cookies.get('XSRF-TOKEN'),
            'x-session-id': cookies.get('x-session-id'),
          },
        },
      );
      return response.data;
      } catch (error) {
    console.error(error);
    return { message: 'Error adding user' };
  }
  },
);



export const getAlluserSessionrigger = createAsyncThunk(
  'user-sessions',
  async ({
    page,
    limit,
    search,
    userId,
    sortOrder,
    sortField,
  }: {
    page?: number;
    limit?: number;
    search?: string;
    userId?: number;
    sortOrder?: string;
    sortField?: string;
  }) => {
    try {
      // Clean the sortField to remove any malformed characters
      const cleanSortField = sortField ? sortField.split(':')[0] : undefined;
      // Use snake_case to match iOS app standard

      const queryParams = new URLSearchParams();
      if (userId) queryParams.append('userId', userId.toString());
      if (page) queryParams.append('page', page.toString());
      if (limit) queryParams.append('limit', limit.toString());
      if (search) queryParams.append('search', search);
      if (sortOrder) queryParams.append('sortOrder', sortOrder);
      if (cleanSortField) queryParams.append('sortField', cleanSortField);

      const response = await api.get(`/user-sessions?${queryParams}`, accessToken());
      return response.data;
    } catch (error) {
      console.log(error);
      return { data: [], totalPages: 0 };
    }
  },
);

// Create slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loginTrigger.pending, (state) => {
        state.loading = true;
      })
      .addCase(loginTrigger.fulfilled, (state, action) => {
        state.loading = false;
        state.loginResponse = action.payload;
      })
      .addCase(loginTrigger.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to load posts';
      })
      .addCase(forgotPasswordTrigger.pending, (state) => {
        state.loading = true;
      })
      .addCase(forgotPasswordTrigger.fulfilled, (state, action) => {
        state.loading = false;
        state.forgotPasswordResponse = action.payload as {
          email: string;
          message: string;
        };
      })
      .addCase(forgotPasswordTrigger.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to request password reset';
      })

      .addCase(resetPasswordTrigger.pending, (state) => {
        state.loading = true;
      })
      .addCase(resetPasswordTrigger.fulfilled, (state, action) => {
        state.loading = false;
        state.resetPasswordResponse = action.payload as { message: string };
      })
      .addCase(resetPasswordTrigger.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to request password reset';
      })
      .addCase(getCSRFToken.pending, (state) => {
        state.loading = true;
      })
      .addCase(getCSRFToken.fulfilled, (state, action) => {
        state.loading = false;
        state.csrfDetails = action.payload;
      })
      .addCase(getCSRFToken.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to request password reset';
      })
      .addCase(getTokenStatus.pending, (state) => {
        state.loading = true;
      })
      .addCase(getTokenStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.tokenExpiry = action.payload;
      })
      .addCase(getTokenStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to request password reset';
      })
      .addCase(otpVerification.pending, (state) => {
        state.loading = true;
      })
      .addCase(otpVerification.fulfilled, (state, action) => {
        state.loading = false;
        state.loginResponse = action.payload;
      })
      .addCase(otpVerification.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to request password reset';
      })
      .addCase(getAlluserActivityTrigger.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAlluserActivityTrigger.fulfilled, (state, action) => {
        state.loading = false;
        state.userActivityList = action.payload;
      })
      .addCase(getAlluserActivityTrigger.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to request password reset';
      })
      .addCase(getAlluserSessionrigger.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAlluserSessionrigger.fulfilled, (state, action) => {
        state.loading = false;
        state.userSessionList = action.payload;
      })
      .addCase(getAlluserSessionrigger.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to request password reset';
      });
  },
});

// Export the reducer to be added to the store
export default authSlice.reducer;
